// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/utils/aiWorkflowTriggers.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

/**
 * src/utils/aiWorkflowTriggers.js
 *
 * Determines which workflows are required for an AI system based on its current
 * state and the resolved risk profile.
 *
 * This is a pure function — no side effects, no API calls. It runs client-side
 * to drive the UI's "pending actions" display and the maturity flags. The
 * server-side mirror (azure-functions/shared/ai/workflowTriggers.js) uses the
 * same logic for the governance sweeper.
 *
 * Rules:
 * - CLASSIFICATION_REQUIRED  — aiActClassification is 'unclassified'
 * - PROHIBITED_SCREENING_REQUIRED — no screening record linked to this system
 * - DPIA_REQUIRED — personalDataExposure or specialCategoryExposure with no linked DPIA
 * - TIA_REQUIRED  — transferOutsideEU with no linked TIA
 * - FRIA_REQUIRED — ONLY when classificationConfirmedAt is set AND
 *                   classification is 'high-risk' (or 'gpai' with gpaiSystemicRisk)
 * - PROFILE_OUTDATED — linked profile version differs from current built-in version
 * - REVIEW_OVERDUE  — system is 'active' and hasn't been reviewed in >365 days
 */

const { isProfileOutdated } = require('./aiRiskProfiles.cjs');

const FRIA_CLASSIFICATIONS = new Set(['high-risk']);

/**
 * AIGOV-B-001: the current Art. 5 screening of an AI system — the one its
 * prohibitedScreeningId names, or null. Every surface that answers "is this
 * system screened?" reads this, so they cannot disagree (Enter Once, Know
 * Everywhere). No back-reference → null: unscreened, never assumed.
 */
function currentScreening(aiSystem, screenings = []) {
  const id = aiSystem && aiSystem.prohibitedScreeningId;
  if (!id) return null;
  return (screenings || []).find((s) => s && s.id === id) || null;
}

/**
 * AIGOV-B-001: a screening PASSED only when it recorded a clear outcome. The
 * legacy `outcome` field counts only when `result` was never written.
 */
function isScreeningClear(screening) {
  if (!screening) return false;
  return screening.result === 'clear' || (screening.result == null && screening.outcome === 'clear');
}

/**
 * The UTC calendar day (YYYY-MM-DD) of a review date, or null when it is
 * absent or unparseable. Date-only and ISO strings are both read; only the
 * calendar date counts, so server and browser agree on the day regardless of
 * time zone (the server is authoritative; a skewed browser clock can only
 * disagree by a day at the edge).
 */
function reviewDay(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const t = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(t.getTime()) ? null : t.toISOString().slice(0, 10);
}

function nowDay(now) {
  const t = now instanceof Date ? now : new Date(now == null ? Date.now() : now);
  return Number.isNaN(t.getTime()) ? null : t.toISOString().slice(0, 10);
}

/**
 * GH #8183 / TRYGGR_INTENT §5, §12: a screening is evidence only while its own
 * review date allows. THE one "is this screening current" check, used by the
 * write gate, the sweeper and every reader (via isCleared). No date, an
 * unparseable date, or a date before today (UTC) is NOT current; the review
 * date itself is still current. `now` defaults to the clock.
 */
function isScreeningCurrent(screening, now) {
  if (!screening) return false;
  const due = reviewDay(screening.nextReviewDate);
  const today = nowDay(now);
  return due !== null && today !== null && due >= today;
}

/**
 * The outcome half only, ignoring the review date. For the one caller that
 * must keep its pre-#8183 meaning (the founder-run stock demotion script).
 */
function isClearedOutcome(aiSystem, screenings = []) {
  if (!aiSystem || aiSystem.aiActClassification === 'prohibited') return false;
  return isScreeningClear(currentScreening(aiSystem, screenings));
}

/**
 * AIGOV-B-001 / TRYGGR_INTENT §5, §6.2: the ONE definition of "may be used".
 * Derived from the register, never from the stored `status` alone: a system is
 * approved for use only when its status is 'active' AND the screening its
 * back-reference names is in the register AND passed clear AND is still
 * current (its review date has not passed — GH #8183) AND it is not
 * classified prohibited. Anything missing or ambiguous reads as NOT approved.
 * The write endpoint (ai-governance-write) gates on `isCleared` — the same
 * function — so the gate and every reader cannot disagree.
 */
function isCleared(aiSystem, screenings = [], now) {
  if (!isClearedOutcome(aiSystem, screenings)) return false;
  return isScreeningCurrent(currentScreening(aiSystem, screenings), now);
}

/**
 * Why a clear screening no longer counts: 'lapsed' (dated, passed) or
 * 'undated' (no usable review date); null when current, absent or not clear.
 */
function screeningLapse(aiSystem, screenings = [], now) {
  if (!isClearedOutcome(aiSystem, screenings)) return null;
  const s = currentScreening(aiSystem, screenings);
  if (isScreeningCurrent(s, now)) return null;
  return reviewDay(s.nextReviewDate) === null ? 'undated' : 'lapsed';
}

/**
 * TRYGGR_INTENT §12 / §5: a TIA approval is evidence only while its own review
 * date allows, exactly as a screening is. THE one "does this TIA cover the
 * transfer" check — status approved, a recorded approver and date, and a review
 * date that has not passed. An approval with no usable review date (every one
 * recorded before review dates existed) is NOT current: it reads as owing the
 * TIA until a person approves it again.
 */
function isTiaCurrent(tia, now) {
  if (!tia || tia.status !== 'approved' || !tia.approverId || !tia.approvedAt) return false;
  // TRYGGR_INTENT §5: an assessment that found the transfer inadequate is not evidence the
  // transfer may proceed, whatever its approval state (defence in depth behind update-tia).
  if (tia.outcome === 'inadequate' || tia.outcome === 'inadequate-blocked') return false;
  // TRYGGR_INTENT §5 / §6.2, GH #8975: a self-approval the tenant never allowed and nobody
  // justified is not evidence the transfer was assessed independently — it does not clear.
  if (tiaSelfApprovalUnrecorded(tia)) return false;
  // Master ruling on GH #8975: a TIA with no recorded author list proves nothing about
  // independence (TRYGGR_INTENT §5). It counts only through a recorded, justified override.
  if (tiaIndependenceUnrecorded(tia) && !tiaSelfApprovalRecorded(tia)) return false;
  const due = reviewDay(tia.nextReviewDate);
  const today = nowDay(now);
  return due !== null && today !== null && due >= today;
}

/** True when a TIA carries a recorded authorship list (substantiveAuthors, at least one person). */
function tiaAuthorsRecorded(tia) {
  return Boolean(tia) && Array.isArray(tia.substantiveAuthors) && tia.substantiveAuthors.some((u) => typeof u === 'string' && u);
}

/**
 * Who wrote the CURRENT version of a TIA's substantive content (GH #8975): every user
 * recorded in substantiveAuthors, which only ever accumulates — a reopen does not clear
 * it, so the original writer can never approve a later revision as "independent" — plus
 * createdBy, always an author even when the list omits them.
 */
function tiaAuthors(tia) {
  if (!tia) return [];
  const listed = Array.isArray(tia.substantiveAuthors) ? tia.substantiveAuthors : [];
  return [...new Set([...listed, tia.createdBy].filter((u) => typeof u === 'string' && u))];
}

/** True when the approver is one of the people who wrote the content they approved. */
function tiaSelfApproved(tia) {
  if (!tia || tia.status !== 'approved' || !tia.approverId) return false;
  return tia.sodOverride === true || tiaAuthors(tia).includes(tia.approverId);
}

/** A self-approval carrying the tenant-permitted, justified override record (sodOverride + justification). */
function tiaSelfApprovalRecorded(tia) {
  return Boolean(tia) && tia.sodOverride === true && typeof tia.sodJustification === 'string' && tia.sodJustification.trim() !== '';
}

/** Approved, yet no authorship list is recorded: independence cannot be shown (even with a createdBy). */
function tiaIndependenceUnrecorded(tia) {
  return Boolean(tia) && tia.status === 'approved' && Boolean(tia.approverId)
    && !tiaAuthorsRecorded(tia);
}

function tiaSelfApprovalUnrecorded(tia) {
  return tiaSelfApproved(tia) && !tiaSelfApprovalRecorded(tia);
}

/**
 * How independent an approval is, for every surface that says so (panel, audit, exports):
 *   'independent'                  approver is not an author (authorship list recorded)
 *   'independence-unrecorded'      no authorship list: never counts as independent. Current only
 *                                  through a recorded override (sodReason 'independence-unrecorded')
 *   'self-approved'                a recorded override on a recorded author: tenant allowed it, justification given
 *   'self-approved-unrecorded'     author approved with no override record (never counts)
 *   null                           not approved
 */
function tiaIndependence(tia) {
  if (!tia || tia.status !== 'approved' || !tia.approverId) return null;
  if (tiaSelfApprovalRecorded(tia)) {
    return tia.sodReason === 'independence-unrecorded' || !tiaAuthorsRecorded(tia) ? 'independence-unrecorded' : 'self-approved';
  }
  if (tiaSelfApproved(tia)) return 'self-approved-unrecorded';
  return tiaIndependenceUnrecorded(tia) ? 'independence-unrecorded' : 'independent';
}

/** Why an approved TIA no longer counts: 'lapsed', 'undated', 'self-approved-unrecorded' or 'independence-unrecorded'; null when current or not approved. */
function tiaLapse(tia, now) {
  if (!tia || tia.status !== 'approved' || !tia.approverId || !tia.approvedAt) return null;
  if (isTiaCurrent(tia, now)) return null;
  if (tiaSelfApprovalUnrecorded(tia)) return 'self-approved-unrecorded';
  if (tiaIndependenceUnrecorded(tia) && !tiaSelfApprovalRecorded(tia)) return 'independence-unrecorded';
  return reviewDay(tia.nextReviewDate) === null ? 'undated' : 'lapsed';
}

function isApprovedForUse(aiSystem, screenings = [], now) {
  return Boolean(aiSystem) && aiSystem.status === 'active' && isCleared(aiSystem, screenings, now);
}

/** What a reader should show for the system's status: 'active' only when approved. */
function displayStatus(aiSystem, screenings = [], now) {
  if (aiSystem && aiSystem.status === 'active' && !isCleared(aiSystem, screenings, now)) {
    // A clear screening whose review date no longer counts is named for what it
    // is — not "not screened", which would misstate the record (TRYGGR_INTENT §5).
    const lapse = screeningLapse(aiSystem, screenings, now);
    if (lapse === 'lapsed') return 'active-lapsed';
    if (lapse === 'undated') return 'active-undated';
    return 'active-unscreened';
  }
  return aiSystem ? aiSystem.status : undefined;
}

/**
 * Returns an array of workflow flag strings indicating what actions are required.
 *
 * @param {object} aiSystem — AI system record from state.aiSystems
 * @param {object|null} effectiveProfile — resolved risk profile (built-in + delta)
 * @param {object} context — { screenings, dpias, tias } arrays from state
 * @returns {string[]}
 */
function detectRequiredWorkflows(aiSystem, effectiveProfile, context = {}) {
  const flags = [];
  const { screenings = [], dpias = [], tias = [], now } = context;

  // 1. Classification
  if (!aiSystem.aiActClassification || aiSystem.aiActClassification === 'unclassified') {
    flags.push('CLASSIFICATION_REQUIRED');
  }

  // 2. Prohibited practices screening
  // AIGOV-B-001: only the screening the system's back-reference names is
  // current. A screening merely ON FILE for the system may predate a material
  // change of use (the server drops the back-reference then), so it is never
  // taken as current — no back-reference means unscreened (fail-safe).
  const current = currentScreening(aiSystem, screenings);
  if (!current) {
    flags.push('PROHIBITED_SCREENING_REQUIRED');
  } else if (!isClearedOutcome(aiSystem, screenings)) {
    // A screening exists but did not pass (or the system is classified
    // prohibited): "screened" is not "cleared" (GH #7726, the #6367 shape).
    flags.push('SCREENING_NOT_CLEAR');
  } else {
    // GH #8183: it passed, but its review date has passed or was never
    // recorded. The owner's review action is this flag, derived live, so it
    // closes only when a new screening is recorded (the back-reference moves
    // to a current one) — never by hand.
    const lapse = screeningLapse(aiSystem, screenings, now);
    if (lapse === 'lapsed') flags.push('SCREENING_REVIEW_DUE');
    else if (lapse === 'undated') flags.push('SCREENING_NO_REVIEW_DATE');
  }

  // 3. DPIA
  const dpiaRequired =
    aiSystem.personalDataExposure ||
    aiSystem.specialCategoryExposure ||
    // The names the Add AI System wizard wrote before QA61 step 9 aligned it
    // with this vocabulary — a record carrying them still owes a DPIA.
    aiSystem.personalDataProcessed === true ||
    aiSystem.specialCategoryData === true ||
    (effectiveProfile && effectiveProfile.riskDimensions?.personalDataProcessing === 'high');
  if (dpiaRequired) {
    const linkedDpia = aiSystem.dpiaId
      ? dpias.some((d) => d.id === aiSystem.dpiaId)
      : dpias.some((d) => d.aiSystemId === aiSystem.id && d.status === 'approved');
    if (!linkedDpia) flags.push('DPIA_REQUIRED');
  }

  // 4. TIA (transfer impact assessment)
  if (aiSystem.transferOutsideEU) {
    const linkedTia = aiSystem.tiaId
      ? tias.some((t) => t.id === aiSystem.tiaId && isTiaCurrent(t, now))
      : tias.some((t) => t.aiSystemId === aiSystem.id && isTiaCurrent(t, now));
    if (!linkedTia) flags.push('TIA_REQUIRED');
  }

  // 5. FRIA — ONLY when classification is confirmed AND is high-risk (or systemic-risk GPAI)
  // Never trigger for 'unclassified', 'limited-risk', or 'minimal-risk'.
  const friaEligible =
    aiSystem.classificationConfirmedAt !== null &&
    aiSystem.classificationConfirmedAt !== undefined &&
    (FRIA_CLASSIFICATIONS.has(aiSystem.aiActClassification) ||
      (aiSystem.aiActClassification === 'gpai' && aiSystem.gpaiSystemicRisk));
  if (friaEligible) {
    const hasApprovedFria = aiSystem.friaId
      ? dpias.some((d) => d.id === aiSystem.friaId && d.dpiaType === 'fria-art27')
      : dpias.some(
          (d) =>
            d.aiSystemId === aiSystem.id && d.dpiaType === 'fria-art27' && d.status === 'approved'
        );
    if (!hasApprovedFria) flags.push('FRIA_REQUIRED');
  }

  // 6. Profile outdated
  if (aiSystem.riskProfileId && effectiveProfile) {
    if (isProfileOutdated(aiSystem)) flags.push('PROFILE_OUTDATED');
  }

  // 7. Review overdue (active systems with updatedAt > 365 days ago)
  if (aiSystem.status === 'active' && aiSystem.updatedAt) {
    const daysSinceUpdate =
      (Date.now() - new Date(aiSystem.updatedAt).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceUpdate > 365) flags.push('REVIEW_OVERDUE');
  }

  return flags;
}

/**
 * Human-readable explanations for each maturity flag.
 * Used in the AI System detail panel and Action Centre.
 */
const MATURITY_FLAG_EXPLANATIONS = {
  CLASSIFICATION_REQUIRED: {
    label: 'Classification needed',
    description:
      'The AI Act classification for this system has not been confirmed. Review the intended use and affected groups to determine the risk category.',
    severity: 'warning',
  },
  SCREENING_NOT_CLEAR: {
    label: 'Screening not cleared',
    description:
      'The prohibited-practices screening on file did not pass, or the system is classified as prohibited. It must not be used until it is re-screened and cleared, or decommissioned.',
    severity: 'critical',
  },
  SCREENING_REVIEW_DUE: {
    label: 'Screening review overdue',
    description:
      "The Article 5 screening's review date has passed, so it no longer counts as current and the system is not approved for use. Record a new screening to restore it.",
    severity: 'critical',
  },
  SCREENING_NO_REVIEW_DATE: {
    label: 'Screening has no review date',
    description:
      'The Article 5 screening on file has no review date recorded, so it cannot be treated as current and the system is not approved for use. Record a new screening with a review date.',
    severity: 'critical',
  },
  PROHIBITED_SCREENING_REQUIRED: {
    label: 'Prohibited practices screening',
    description:
      'Complete the Article 5 screening to confirm this AI system does not engage in prohibited practices.',
    severity: 'required',
  },
  DPIA_REQUIRED: {
    label: 'DPIA required',
    description:
      'This system processes personal data. A Data Protection Impact Assessment is required under GDPR Article 35.',
    severity: 'required',
  },
  TIA_REQUIRED: {
    label: 'Transfer Impact Assessment required',
    description:
      'This system transfers personal data outside the EU. A Transfer Impact Assessment is required to validate the transfer mechanism. An approval stops counting when its review date passes — approve it again to renew.',
    severity: 'required',
  },
  FRIA_REQUIRED: {
    label: 'Fundamental Rights Impact Assessment required',
    description:
      'This system is classified as high-risk under the EU AI Act. A Fundamental Rights Impact Assessment (Art. 27) is required before deployment.',
    severity: 'required',
  },
  PROFILE_OUTDATED: {
    label: 'Risk profile updated',
    description:
      'The built-in risk profile for this AI tool has been updated. Review the changes and confirm the profile is still appropriate for your use case.',
    severity: 'info',
  },
  REVIEW_OVERDUE: {
    label: 'Annual review overdue',
    description:
      'This AI system has not been reviewed in over a year. Confirm the configuration and risk assessment are still current.',
    severity: 'warning',
  },
};

module.exports = {
  currentScreening,
  isScreeningClear,
  isScreeningCurrent,
  isClearedOutcome,
  screeningLapse,
  isTiaCurrent,
  tiaLapse,
  tiaAuthors,
  tiaAuthorsRecorded,
  tiaSelfApproved,
  tiaSelfApprovalRecorded,
  tiaIndependence,
  reviewDay,
  isCleared,
  isApprovedForUse,
  displayStatus,
  detectRequiredWorkflows,
  MATURITY_FLAG_EXPLANATIONS,
};
